/**
 * ColorThemeProvider — applies one of the 10 global color themes by setting
 * `data-color-theme="<name>"` on <html>, ALONGSIDE the existing light/`.dark`
 * class managed by ThemeProvider. The two are orthogonal: light/dark picks the
 * brightness, color-theme picks the hue family. CSS in index.css combines them
 * (`[data-color-theme="x"]` for light, `.dark[data-color-theme="x"]` for dark).
 *
 * Initialization order:
 *   1. localStorage (immediate, before paint — see applyColorThemeFromStorage in main).
 *   2. backend user preferences (cross-device) — reconciled on load; backend wins
 *      if present, then mirrored back into localStorage.
 *
 * Persistence on change: localStorage immediately + PUT to settings-service
 * (display.colorTheme) so the choice follows the user across devices/re-login.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react'

import {
  type ThemeName,
  THEME_STORAGE_KEY,
  DEFAULT_THEME,
  isThemeName,
} from '@/lib/themes'
import {
  useUserPreferences,
  useUpdateUserPreferences,
} from '@/services/settings.service'

interface ColorThemeContextValue {
  colorTheme: ThemeName
  setColorTheme: (name: ThemeName) => void
}

const ColorThemeContext = createContext<ColorThemeContextValue | undefined>(undefined)

function readStoredColorTheme(): ThemeName {
  if (typeof window === 'undefined') return DEFAULT_THEME
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (isThemeName(stored)) return stored
  } catch {
    /* localStorage unavailable */
  }
  return DEFAULT_THEME
}

/** Imperatively apply the attribute to <html>. */
export function applyColorTheme(name: ThemeName) {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-color-theme', name)
}

interface ColorThemeProviderProps {
  children: ReactNode
}

export function ColorThemeProvider({ children }: ColorThemeProviderProps) {
  const [colorTheme, setColorThemeState] = useState<ThemeName>(() => readStoredColorTheme())

  // Backend reconcile (best-effort — never blocks the UI).
  const { data: prefs } = useUserPreferences()
  const updatePrefs = useUpdateUserPreferences()
  const reconciledRef = useRef(false)

  // Keep the <html> attribute in sync with state.
  useEffect(() => {
    applyColorTheme(colorTheme)
  }, [colorTheme])

  // Sync when the accent is changed elsewhere (e.g. command palette writes the
  // THEME_STORAGE_KEY + dispatches a `storage` event). Keeps the switcher's
  // active swatch and this provider in agreement without a remount.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== THEME_STORAGE_KEY) return
      if (isThemeName(e.newValue)) setColorThemeState(e.newValue)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // On first successful backend load, reconcile (backend wins) and mirror to localStorage.
  useEffect(() => {
    if (reconciledRef.current) return
    if (!prefs) return
    reconciledRef.current = true
    const backendTheme = (prefs as Record<string, unknown>).colorTheme
    if (isThemeName(backendTheme) && backendTheme !== colorTheme) {
      setColorThemeState(backendTheme)
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, backendTheme)
      } catch {
        /* ignore */
      }
    }
  }, [prefs, colorTheme])

  const setColorTheme = useCallback(
    (name: ThemeName) => {
      if (!isThemeName(name)) return
      setColorThemeState(name)
      // Immediate local persistence.
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, name)
      } catch {
        /* ignore */
      }
      // Cross-device persistence (best-effort; ignore failures so the UI never breaks).
      try {
        // `colorTheme` is stored as a display preference (settings-service display category).
        updatePrefs.mutate({ colorTheme: name } as never)
      } catch {
        /* ignore */
      }
    },
    [updatePrefs],
  )

  return (
    <ColorThemeContext.Provider value={{ colorTheme, setColorTheme }}>
      {children}
    </ColorThemeContext.Provider>
  )
}

export function useColorTheme(): ColorThemeContextValue {
  const ctx = useContext(ColorThemeContext)
  if (!ctx) {
    throw new Error('useColorTheme must be used within a ColorThemeProvider')
  }
  return ctx
}
